import React, { useEffect, useState } from "react";
import { Mail, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function EmailSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/email/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ configured: false }))
      .finally(() => setLoading(false));
  }, []);

  const sendTest = async () => {
    if (!recipientEmail.trim()) {
      toast.error("Renseignez une adresse de destination pour le test");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/email/test-confirmation", {
        recipient_email: recipientEmail.trim(),
      });
      setLastResult(data);
      if (data.sent) {
        toast.success(`Email envoyé à ${recipientEmail}. Vérifiez votre boîte (et les spams).`);
      } else {
        toast.error(`Échec: ${data.error || "voir détail ci-dessous"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
      setLastResult({ ok: false, error: e?.response?.data?.detail || "request_failed" });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1F4E3D]" />
      </div>
    );
  }

  const configured = status?.configured;
  const testMode = status?.test_mode;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="email-settings-page">
      <div className="flex items-center gap-3 mb-2">
        <Mail className="w-6 h-6 text-[#1F4E3D]" />
        <h1 className="text-3xl font-bold text-[#1A1C1A]">Emails de confirmation client</h1>
      </div>
      <p className="text-sm text-[#575A57] mb-8 max-w-2xl">
        Envoyés automatiquement au client dès qu'une commande boutique est payée sur PayPal.
        Service utilisé : <strong>Resend</strong>. Vous recevez aussi une copie discrète (BCC) sur info@maison-thai.ch.
      </p>

      {/* Statut configuration */}
      <div
        className={`border-l-4 p-5 mb-8 rounded-r ${
          configured
            ? testMode ? "border-[#C1A063] bg-[#FBF4E9]" : "border-[#1F4E3D] bg-[#EAF3EE]"
            : "border-[#C05C47] bg-[#FBEAE6]"
        }`}
        data-testid="email-config-status"
      >
        <div className="flex items-start gap-3">
          {configured
            ? <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${testMode ? "text-[#8C6B2F]" : "text-[#1F4E3D]"}`} />
            : <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-[#C05C47]" />
          }
          <div className="flex-1">
            {configured ? (
              testMode ? (
                <>
                  <p className="font-semibold text-[#8C6B2F]">Mode test Resend actif</p>
                  <p className="text-sm text-[#8C6B2F] mt-1">
                    Expéditeur : <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">{status.sender_email}</code>.
                    Dans ce mode, les emails ne peuvent partir que vers votre propre adresse Resend.
                    Pour pouvoir envoyer à vos clients, vérifiez votre domaine <strong>maison-thai.ch</strong> sur Resend
                    puis changez <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">SENDER_EMAIL</code> en <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">info@maison-thai.ch</code> dans les Custom Keys.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-[#1F4E3D]">Configuration de production active</p>
                  <p className="text-sm text-[#1F4E3D]/80 mt-1">
                    Expéditeur : <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">{status.sender_email}</code>.
                    {status.reply_to && (
                      <> Adresse de réponse : <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">{status.reply_to}</code>.</>
                    )}
                    {" "}Les emails sont envoyés depuis votre domaine vérifié — délivrabilité optimale.
                  </p>
                  {status.sender_email && status.sender_email.endsWith("@send.maison-thai.ch") && (
                    <p className="text-xs text-[#8C6B2F] mt-2 italic">
                      💡 Mode bypass actif : l'expéditeur est sur le sous-domaine <code className="font-mono bg-white/60 px-1 rounded">send.maison-thai.ch</code> (Resend natif) pour éviter le rejet self-loop de Webhostingpad. Les réponses des clients arrivent bien sur <code className="font-mono bg-white/60 px-1 rounded">{status.reply_to || "info@maison-thai.ch"}</code>.
                    </p>
                  )}
                </>
              )
            ) : (
              <>
                <p className="font-semibold text-[#C05C47]">Resend non configuré</p>
                <p className="text-sm text-[#7d3d33] mt-1">
                  Ajoutez les Custom Keys <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">RESEND_API_KEY</code> et <code className="font-mono text-xs bg-white/60 px-1.5 py-0.5 rounded">SENDER_EMAIL</code>, puis redéployez.
                  Tant que ce n'est pas fait, les emails clients ne partent pas (mais les paiements PayPal continuent de fonctionner normalement).
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Test d'envoi */}
      <div className="bg-white border border-[#E5E5E0] rounded-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-[#1A1C1A] mb-2">Tester l'envoi</h2>
        <p className="text-sm text-[#575A57] mb-5">
          Envoie un email de confirmation factice à l'adresse ci-dessous pour vérifier le rendu visuel
          et la délivrabilité (pensez à regarder dans les <strong>spams</strong> si rien n'arrive).
        </p>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#575A57] mb-1.5 block">
              Email de destination
            </Label>
            <Input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="votre.email@example.ch"
              disabled={!configured || sending}
              data-testid="email-test-recipient"
            />
            <p className="text-xs text-[#A3A6A3] mt-1">
              {testMode
                ? "En mode test Resend, mettez ici l'adresse de votre compte Resend uniquement."
                : "L'adresse qui recevra le mail de test."}
            </p>
          </div>

          <Button
            onClick={sendTest}
            disabled={!configured || sending || !recipientEmail.trim()}
            className="bg-[#1F4E3D] hover:bg-[#163829] text-white"
            data-testid="email-test-send-btn"
          >
            {sending
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
              : <><Send className="w-4 h-4 mr-2" /> Envoyer un email de test</>
            }
          </Button>
        </div>

        {lastResult && (
          <div
            className={`mt-5 p-3 rounded border text-sm ${
              lastResult.sent
                ? "border-[#1F4E3D]/30 bg-[#EAF3EE] text-[#1F4E3D]"
                : "border-[#C05C47]/30 bg-[#FBEAE6] text-[#7d3d33]"
            }`}
            data-testid="email-test-result"
          >
            {lastResult.sent ? (
              <>
                <strong>Envoi réussi.</strong> ID Resend :{" "}
                <code className="font-mono text-xs">{lastResult.id}</code>
                <p className="text-xs mt-1">Vérifiez votre boîte de réception (et le dossier spam).</p>
              </>
            ) : (
              <>
                <strong>Échec :</strong> {lastResult.error || "voir logs serveur"}
              </>
            )}
          </div>
        )}
      </div>

      {/* Aide */}
      <details className="bg-[#FAFAF8] border border-[#E5E5E0] rounded-md p-5">
        <summary className="cursor-pointer text-sm font-semibold text-[#1A1C1A]">
          Comment passer en mode production (envoyer depuis info@maison-thai.ch) ?
        </summary>
        <ol className="mt-3 text-sm text-[#575A57] space-y-2 list-decimal list-inside">
          <li>Sur <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#1F4E3D] underline">resend.com/domains</a>, cliquez « Add Domain » et entrez <strong>maison-thai.ch</strong>.</li>
          <li>Resend affiche 3-4 enregistrements DNS (MX, SPF TXT, DKIM TXT, optionnellement DMARC).</li>
          <li>Ajoutez ces enregistrements chez votre registrar (GoDaddy) sur la zone DNS de <strong>maison-thai.ch</strong>.</li>
          <li>Attendez la propagation (15 min - 24h). Resend coche les records en vert au fur et à mesure.</li>
          <li>Une fois le domaine vérifié, changez la Custom Key <code className="font-mono text-xs bg-white px-1 rounded">SENDER_EMAIL</code> en <code className="font-mono text-xs bg-white px-1 rounded">info@maison-thai.ch</code>, puis redéployez.</li>
        </ol>
      </details>
    </div>
  );
}
